import {useDataContext} from "../../SurveyContextProvider";
import {useAuthData} from "../../../App";
import {useSnackbar} from "../../../Content";
import {useIntl} from "react-intl";
import {PostSurveyWithId, PostUsersToAnnieUserSurvey, UpdateSurveyWithIdAndUpdateContext} from "../../../api/APISurvey";
import {useRef, useState} from "react";
import {GetAssistant, PostAssistant, PostAssistantWithId, CreateVectorStore, AddFileToVectorStore, RemoveFileFromVectorStore, GetVectorStoreFiles} from "../../../api/APIBot";
import {useAssistantOperations, gatherFilesFromBots} from "../../../utils/assistantUtils";
import {clearValueFromSessionStorage, reconstructConfigObject, removeUnnecessaryKeys} from "../../../DataFunctions";
import _ from "lodash";
import {updateTimeToServerFormat} from "../../../Formats";

export function useSurveySave() {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData');
    const { value: userSurveyData } = useDataContext('userSurveyData');
    const { value: originalSurveyData, setValue: setOriginalSurveyData } = useDataContext('originalSurveyData');
    const { value: originalUserSurveyData, setValue: setOriginalUserSurveyData } = useDataContext('originalUserSurveyData');
    const { value: supportBots } = useDataContext('supportBots');

    const { authData } = useAuthData();
    const { setSnackbarData } = useSnackbar();
    const intl = useIntl();

    // Local loading state to track save operation from start to finish (null, 'DRAFT', or 'ACTIVE')
    const [isSaving, setIsSaving] = useState(null);

    const surveyUpdate = UpdateSurveyWithIdAndUpdateContext(surveyData.id, setSurveyData, setOriginalSurveyData);
    const newID = useRef(new Date().getTime().toString());
    const surveyPost = PostSurveyWithId(newID.current);
    const annieUserSurveyPost = PostUsersToAnnieUserSurvey();
    const assistantId = surveyData?.config?.assistantid || "";
    const postAssistantWithId = PostAssistantWithId(assistantId);
    const postAssistant = PostAssistant();
    const getAssistant = GetAssistant(assistantId, surveyData?.config?.type === "SUPPORTBOT");

    // Vector store hooks for survey-specific file management
    const createVectorStore = CreateVectorStore();
    const addFileToVectorStore = AddFileToVectorStore();
    const removeFileFromVectorStore = RemoveFileFromVectorStore();
    const { refetch: refetchVectorStoreFiles } = GetVectorStoreFiles(
        surveyData?.config?.vector_store_id,
        false
    );

    // Use the shared hook for assistant operations
    const { buildAssistantInstructions, fetchCombinedToolsAndResources } = useAssistantOperations();

    const clearSessionStorage = (newId) => {
        clearValueFromSessionStorage('surveyData', surveyData.id);
        clearValueFromSessionStorage('userSurveyData', surveyData.id);
        clearValueFromSessionStorage('surveyData', 'new');
        clearValueFromSessionStorage('userSurveyData', 'new');
        if (newId) {
            clearValueFromSessionStorage('surveyData', newId);
            clearValueFromSessionStorage('userSurveyData', newId);
        }
    };

    const handleSaveError = (error) => {
        setSnackbarData({
            show: true,
            text: intl.formatMessage({
                id: 'toast.saveFailed',
                defaultMessage: 'The save failed. Please try again.',
            }),
            state: "error",
            autoHide: true,
        });
    };

    const handleSaveSuccess = (callback) => {
        setSnackbarData({
            show: true,
            text: intl.formatMessage({
                id: 'toast.saveSuccess',
                defaultMessage: 'Survey Saved!',
            }),
            state: "success",
            autoHide: true,
        });
        if (callback) callback();
    };

    const processSurveyUpdate = (isNewSurvey, isDraft, callback, cleanedSurveyData) => {
        if (isNewSurvey) {
            cleanedSurveyData.id = newID.current;
            const surveyDataForPost = reconstructConfigObject(cleanedSurveyData);

            surveyPost.mutate(surveyDataForPost, {
                onError: handleSaveError,
                onSuccess: (response) => {
                    if (userSurveyData.length < 1) {
                        clearSessionStorage();
                        if (callback) {
                            callback(newID.current);
                        }
                    } else {
                        const newAnnieUserSurveyData = [...userSurveyData];
                        newAnnieUserSurveyData.forEach(annieuser => annieuser.survey = newID.current);
                        annieUserSurveyPost.mutate(newAnnieUserSurveyData, {
                            onError: handleSaveError,
                            onSuccess: () => {
                                clearSessionStorage();
                                if (callback) callback(newID.current);
                            }
                        });
                    }
                }
            });
        } else {
            const surveyDataForPost = reconstructConfigObject(cleanedSurveyData);

            surveyUpdate.mutate(surveyDataForPost, {
                onError: handleSaveError,
                onSuccess: () => {
                    if (userSurveyData.length < 1) {
                        setOriginalUserSurveyData(_.cloneDeep(userSurveyData));
                        clearSessionStorage();
                        handleSaveSuccess(callback);
                    } else {
                        annieUserSurveyPost.mutate(userSurveyData, {
                            onError: handleSaveError,
                            onSuccess: () => {
                                setOriginalUserSurveyData(_.cloneDeep(userSurveyData));
                                clearSessionStorage();
                                handleSaveSuccess(callback);
                            }
                        });
                    }
                }
            });
        }
    };

    const handleSurveyVectorStore = async (surveyDataToSave, supportBots) => {
        const selectedBotIds = surveyDataToSave?.config?.supportBots || [];

        // Gather all file IDs from selected bot modules
        const fileIds = gatherFilesFromBots(selectedBotIds, supportBots);

        // If no files, return null (don't create vector store)
        if (fileIds.length === 0) {
            return null;
        }

        let currentVectorStoreId = surveyDataToSave.config?.vector_store_id;

        // Create vector store if it doesn't exist
        if (!currentVectorStoreId) {
            try {
                const createRes = await createVectorStore.mutateAsync({});
                currentVectorStoreId = createRes?.id;
                if (!currentVectorStoreId) {
                    throw new Error("Vector store creation failed - no ID in response");
                }
            } catch (error) {
                console.error("Error creating survey vector store:", error);
                throw error;
            }
        }

        // Sync files to the vector store
        try {
            let existingFileIds = new Set();

            // Fetch existing files if vector store already existed
            if (surveyDataToSave.config?.vector_store_id) {
                const { data: currentFiles } = await refetchVectorStoreFiles();
                existingFileIds = new Set(currentFiles?.data?.map(f => f.id) || []);
            }

            const newFileIds = new Set(fileIds);

            // Add new files
            for (const fileId of fileIds) {
                if (!existingFileIds.has(fileId)) {
                    console.log(`Adding file ${fileId} to survey vector store ${currentVectorStoreId}`);
                    await addFileToVectorStore.mutateAsync({
                        vectorStoreId: currentVectorStoreId,
                        fileId
                    });
                }
            }

            // Remove files that are no longer in any selected bot
            for (const existingFileId of existingFileIds) {
                if (!newFileIds.has(existingFileId)) {
                    console.log(`Removing file ${existingFileId} from survey vector store ${currentVectorStoreId}`);
                    await removeFileFromVectorStore.mutateAsync({
                        vectorStoreId: currentVectorStoreId,
                        fileId: existingFileId
                    });
                }
            }
        } catch (error) {
            console.error("Error syncing files with survey vector store:", error);
            throw error;
        }

        return currentVectorStoreId;
    };

    const saveSurvey = async (isNewSurvey, isDraft, callback, saveType = null) => {
        setIsSaving(saveType || (isDraft ? 'DRAFT' : 'ACTIVE'));

        const newSurveyData = {...surveyData};
        const cleanedSurveyData = removeUnnecessaryKeys(newSurveyData);

        cleanedSurveyData.updated = updateTimeToServerFormat(new Date());
        cleanedSurveyData.updatedby = authData.uid;

        if (isDraft) {
            cleanedSurveyData.status = "DRAFT";
        } else if (cleanedSurveyData.status === "DRAFT") {
            cleanedSurveyData.status = "SCHEDULED";
        }

        setSurveyData(cleanedSurveyData);

        if (surveyData?.config?.type === "SUPPORTBOT") {
            try {
                // 1. Handle survey vector store creation and file syncing
                let surveyVectorStoreId = null;
                try {
                    surveyVectorStoreId = await handleSurveyVectorStore(cleanedSurveyData, supportBots);
                    if (surveyVectorStoreId) {
                        cleanedSurveyData.config.vector_store_id = surveyVectorStoreId;
                    }
                } catch (error) {
                    setIsSaving(null);
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage({
                            id: 'vectorStoreSaveFailed',
                            defaultMessage: 'Failed to save reference materials. Please try again.'
                        }),
                        state: "error",
                        autoHide: true,
                    });
                    return; // Stop save if vector store fails
                }

                // 2. Build assistant instructions using shared function
                const surveyId = surveyData?.id === "new" ? newID.current : surveyData?.id;
                const processedAssistantData = buildAssistantInstructions(surveyData, surveyId, false);

                // 3. Fetch combined tools from bot modules
                let combinedTools = [];
                try {
                    const { tools } = await fetchCombinedToolsAndResources(surveyData?.config?.supportBots);
                    if (tools) {
                        combinedTools = tools;
                    }
                } catch (error) {
                    console.error('Error fetching assistant tools:', error);
                    // Continue without tools if there's an error
                }

                // 4. Ensure file_search tool is present if we have files
                const selectedBotIds = cleanedSurveyData?.config?.supportBots || [];
                const fileIds = gatherFilesFromBots(selectedBotIds, supportBots);

                let tools = combinedTools;
                if (fileIds.length > 0) {
                    const hasFileSearchTool = tools.some(tool => tool.type === "file_search");
                    if (!hasFileSearchTool) {
                        tools = [...tools, { type: "file_search" }];
                    }
                } else {
                    // Remove file_search tool if no files
                    tools = tools.filter(tool => tool.type !== "file_search");
                }

                processedAssistantData.tools = tools;

                // 5. Add vector store to tool_resources if available
                if (surveyVectorStoreId) {
                    processedAssistantData.tool_resources = {
                        file_search: {
                            vector_store_ids: [surveyVectorStoreId]
                        }
                    };
                }

                // 6. Create or update assistant
                if (assistantId) {
                    if (getAssistant.isLoading) {
                        setIsSaving(null);
                        return;
                    } else if (getAssistant.isSuccess && getAssistant.data) {
                        // Update the existing assistant
                        postAssistantWithId.mutate(processedAssistantData, {
                            onError: (error) => {
                                setIsSaving(null);
                                handleSaveError(error);
                            },
                            onSuccess: () => {
                                setIsSaving(null);
                                processSurveyUpdate(isNewSurvey, isDraft, callback, cleanedSurveyData);
                            }
                        });
                    } else if (getAssistant.isError) {
                        setIsSaving(null);
                        handleSaveError();
                    }
                } else {
                    // Create a new assistant
                    postAssistant.mutate(processedAssistantData, {
                        onError: (error) => {
                            setIsSaving(null);
                            handleSaveError(error);
                        },
                        onSuccess: (response) => {
                            setIsSaving(null);
                            if (response && response?.data?.id) {
                                cleanedSurveyData.config.assistantid = response.data.id;
                                setSurveyData(cleanedSurveyData);
                            }
                            processSurveyUpdate(isNewSurvey, isDraft, callback, cleanedSurveyData);
                        }
                    });
                }
            } catch (error) {
                console.error("Error saving SUPPORTBOT assistant:", error);
                setIsSaving(null);
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: 'assistantSaveFailed',
                        defaultMessage: 'Failed to save assistant. Please try again.'
                    }),
                    state: "error",
                    autoHide: true,
                });
                return; // Stop save if assistant creation fails
            }
        } else {
            // Non-SUPPORTBOT surveys proceed directly
            setIsSaving(null);
            processSurveyUpdate(isNewSurvey, isDraft, callback, cleanedSurveyData);
        }
    };

    return {
        saveSurvey,
        clearSessionStorage,
        isSaving,
        isLoading: isSaving !== null || surveyUpdate.isLoading || surveyPost.isLoading ||
            postAssistantWithId.isLoading || postAssistant.isLoading
    };
}